import { Box, CardsProps, Header, SpaceBetween } from "@amzn/awsui-components-react";
import { Button } from "@amzn/uxm-aws-ui-components-react/lib/src/polaris/";
import { CardHeaderWithQuantityInput } from "hooks/useCardTable";
import {
    NonStandardHardwareFormat,
    NonstandardOrderCardSections,
    NonStandardHardwareOrderConfirmationProps,
} from "interfaces";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import CardTable from "common/component/CardTable/CardTable";
import useNonStandardOrder, { generateNonStandardErrorId } from "hooks/useNonstandardOrder";
import ConfirmActionButton from "common/component/ConfirmActionButton/ConfirmActionButton";
import CustomTextFormField from "common/component/CustomFormField/CustomTextFormField";
import { NON_STANDARD_ITEM_FIELD_SELECTORS, NON_STANDARD_ORDER } from "helpers";

const getStringPath: string = "common.component.LocalItOrderConfirmationContent";
const getStringPathCard: string = `${getStringPath}.nonStandardOrder`;
const getStringPathRemove: string = `${getStringPathCard}.removeModal`;

/**
 * Defines which sections should be visible in the card
 *
 * Used to control which information is displayed for each non standard hardware item
 * @type {string[]}
 */
export const NonstandardOrderVisibleCardSections: string[] = [
    NonstandardOrderCardSections.MANUFACTURER,
    NonstandardOrderCardSections.SKU,
    NonstandardOrderCardSections.LINK,
    NonstandardOrderCardSections.DESCRIPTION,
    NonstandardOrderCardSections.QUANTITY,
    NonstandardOrderCardSections.CUSTOM_BUTTON,
];

/**
 * Creates a card definition object for rendering non standard hardware item cards
 *
 * @param {(id: string) => string} getItemName - Function that gets the item name to display
 * @param {(id: string) => void} removeItem - Function that removes an item from the cards
 * @param {(id: string, updates: Partial<NonStandardHardwareFormat>) => void} updateNonStandardHardware -
 * Function that updates a partial of the item
 * @param {() => boolean} canRemoveItems - Function that returns if items can be removed
 * @param {(quantity: number) => number} enforceMinimumQuantity - Function that enforces the minimum quantity allowed
 * @param {(id: string) => string | undefined} getHasError - Function that returns an error text if the section doesn't comply with the
 * requirements needed for the form
 *
 * @returns {object} Card definition object containing header and sections configuration
 */
export const getCardDefinitionNonStandard = (
    getItemName: (id: string) => string,
    removeItem: (id: string) => void,
    updateNonStandardHardware: (id: string, updates: Partial<NonStandardHardwareFormat>) => void,
    canRemoveItems: () => boolean,
    enforceMinimumQuantity: (quantity: number) => number,
    getHasError: (id: string) => string | undefined,
): CardsProps.CardDefinition<NonStandardHardwareFormat> => ({
    header: (item) => (
        <CardHeaderWithQuantityInput
            name={getItemName(item.id)}
            description={""}
            value={item.quantity}
            setValue={(value: number) =>
                updateNonStandardHardware(item.id, {
                    quantity: enforceMinimumQuantity(value),
                })
            }
            quantityErrorText={getHasError(generateNonStandardErrorId(item.id, "quantity"))}
            touchpoint="intake-non-standard-item-quantity"
        />
    ),
    sections: [
        {
            id: NonstandardOrderCardSections.MANUFACTURER,
            content: (item) => (
                <CustomTextFormField
                    testId={NON_STANDARD_ITEM_FIELD_SELECTORS.MANUFACTURER}
                    label={getString(`${getStringPathCard}.manufacturer.label`)}
                    placeholder={getString(`${getStringPathCard}.manufacturer.placeholder`)}
                    setValue={(detail) => updateNonStandardHardware(item.id, { manufacturer: detail })}
                    value={item.manufacturer}
                    errorText={getHasError(generateNonStandardErrorId(item.id, "manufacturer"))}
                    touchpoint="intake-non-standard-item-manufacturer"
                />
            ),
        },
        {
            id: NonstandardOrderCardSections.SKU,
            content: (item) => (
                <CustomTextFormField
                    testId={NON_STANDARD_ITEM_FIELD_SELECTORS.SKU}
                    label={getString(`${getStringPathCard}.sku.label`)}
                    placeholder={getString(`${getStringPathCard}.sku.placeholder`)}
                    setValue={(detail) => updateNonStandardHardware(item.id, { sku: detail })}
                    value={item.sku}
                    errorText={getHasError(generateNonStandardErrorId(item.id, "sku"))}
                    touchpoint="intake-non-standard-item-sku"
                />
            ),
        },
        {
            id: NonstandardOrderCardSections.LINK,
            content: (item) => {
                return (
                    <CustomTextFormField
                        testId={NON_STANDARD_ITEM_FIELD_SELECTORS.LINK}
                        label={getString(`${getStringPathCard}.productLink.label`)}
                        placeholder={getString(`${getStringPathCard}.productLink.placeholder`)}
                        setValue={(detail) => updateNonStandardHardware(item.id, { link: detail })}
                        value={item.link}
                        touchpoint="intake-non-standard-item-link"
                    />
                );
            },
        },
        {
            id: NonstandardOrderCardSections.DESCRIPTION,
            content: (item) => (
                <CustomTextFormField
                    testId={NON_STANDARD_ITEM_FIELD_SELECTORS.DESCRIPTION}
                    label={getString(`${getStringPathCard}.productDescription.label`)}
                    placeholder={getString(`${getStringPathCard}.productDescription.placeholder`)}
                    setValue={(detail) => updateNonStandardHardware(item.id, { description: detail })}
                    value={item.description}
                    variant="textArea"
                    touchpoint="intake-non-standard-item-description"
                />
            ),
        },
        {
            id: NonstandardOrderCardSections.CUSTOM_BUTTON,
            content: (item) => (
                <Box float="right">
                    <ConfirmActionButton
                        iconAlign="left"
                        iconName="subtract-minus"
                        content={getString(`${getStringPathCard}.removeButton`)}
                        onClick={() => removeItem(item.id)}
                        disabled={!canRemoveItems()}
                        disabledReason={getString(`${getStringPathCard}.removeLimitReason`)}
                        modalTitle={getString("title", getStringPathRemove, { position: getItemName(item.id) })}
                        modalCancel={getString(`${getStringPathRemove}.cancel`)}
                        modalConfirm={getString(`${getStringPathRemove}.confirm`)}
                        modalDescription={getString(`${getStringPathRemove}.description`)}
                        touchpoint="intake-remove-non-standard-item"
                    />
                </Box>
            ),
        },
    ],
});

/**
 * Renders the non standard order layout
 *
 * @property {CardsProps.CardsLayout[]} cardsLayout - The cards layout configuration using cloudscape
 * @property {NonStandardHardwareFormat[]} nonStandardOrder - The list of non standard orders to display
 * @property {React.Dispatch<React.SetStateAction<NonStandardHardwareFormat[]>>} setNonStandardOrder - The setter function of
 * the nonStandardOrder property
 *
 * @returns {JSX.Element} An render that allows the user to view and add non standard orders
 */
const NonStandardHardwareOrderConfirmation = ({
    getHasError,
    cardsLayout,
    nonStandardOrder,
    setNonStandardOrder,
}: NonStandardHardwareOrderConfirmationProps) => {
    /* Non Standard Order Hook */
    const {
        catalog,
        trackBy,
        updateNonStandardHardware,
        removeItem,
        getItemName,
        addNonStandardHardware,
        canAddMore,
        canRemoveItems,
        enforceMinimumQuantity,
    } = useNonStandardOrder({
        nonStandardOrder,
        setNonStandardOrder,
    });

    /**
     * The hook doesn't have long operations.
     * The use of a loading state causes unnecessary ui changes between loaded and loading.
     * Thats why is set to false.
     */
    const loading = false;

    return (
        <SpaceBetween size="m">
            <CardTable<NonStandardHardwareFormat>
                cardDefinitions={getCardDefinitionNonStandard(
                    getItemName,
                    removeItem,
                    updateNonStandardHardware,
                    canRemoveItems,
                    enforceMinimumQuantity,
                    getHasError,
                )}
                catalog={catalog}
                cardSectionsVisible={NonstandardOrderVisibleCardSections}
                loading={loading}
                trackBy={trackBy}
                header={<Header>{getString(`${getStringPath}.header.title`)}</Header>}
                cardsLayout={cardsLayout}
            />
            <Box float="right">
                <Button
                    data-testid={NON_STANDARD_ORDER.ADD_ITEM}
                    variant="primary"
                    iconAlign="left"
                    iconName="add-plus"
                    onClick={addNonStandardHardware}
                    disabled={!canAddMore}
                    disabledReason={getString(`${getStringPathCard}.addLimitReason`)}
                    touchpoint="intake-add-non-standard-item"
                >
                    {getString(`${getStringPathCard}.addButton`)}
                </Button>
            </Box>
        </SpaceBetween>
    );
};

export default NonStandardHardwareOrderConfirmation;
